import { shortUsd, type DirectionalEdge } from '@/lib/directionalEdge';
/****************
 * The three portfolio charts, defined once.
 *
 * Guillaume, 2026-09-03: "these 3 things should be presented the exact same way
 * on the dashboard, the trader's profile for all trader and for the specific
 * trader. Same order, same colour code, same display type."
 *
 * They were not. The dashboard drew asset split as a donut; the trader page drew
 * the same quantity as a list of bars. Two pictures of one idea make a reader
 * work out whether they are the same idea — and here they were, so the work was
 * wasted.
 *
 * The rule this file enforces is that sameness cannot be maintained by
 * agreement. Three copies of a donut that "look the same today" diverge the
 * first time one of them is touched. So there is one donut, one gauge and one
 * palette, and every caller passes DATA — never colours, never geometry.
 ****************/

import { dialMarks, needleTip, valueArcPath } from '@/lib/leverageDial.core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtFixed, fmtPct } from '@/lib/format';
import type { AssetClass } from '@/lib/instruments';
import { SymbolLabel } from '@/components/AssetType';

/** Slice colours, in assignment order. Shared so an asset in first place is the
 *  same blue in every ring on every page. */
export const ASSET_COLORS = [
  'hsl(217, 91%, 60%)',
  'hsl(262, 83%, 66%)',
  'hsl(190, 90%, 45%)',
  'hsl(43, 90%, 55%)',
  'hsl(330, 75%, 60%)',
  'hsl(160, 60%, 45%)',
];
export const REST_COLOR = 'hsl(220, 10%, 40%)';
export const LONG_COLOR = 'hsl(152, 60%, 40%)';
export const SHORT_COLOR = 'hsl(0, 70%, 55%)';
export const WARN_COLOR = 'hsl(38, 75%, 48%)';
export const TRACK_COLOR = 'hsl(220, 14%, 18%)';

/** Slices beyond this fold into one "+N more" wedge, so the ring stays legible
 *  and the card is one height whether the book holds four assets or forty. The
 *  tail is not dropped — the centre total still includes it. */
const TOP_SLICES = 5;

const RADIUS = 38;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface Slice {
  symbol: string;
  value: number;
  /** The name shown in the legend, when it is not the asset itself (a type: "Stocks/ETFs"). */
  label?: string;
  /** A slice that IS an asset type takes that type's colour, the one its badge uses everywhere. */
  kind?: AssetClass;
}

/** An asset type's colour, the same as its badge (Guillaume, 2026-10-04: crypto
 *  orange, stock blue, commodities green). */
export const TYPE_COLORS: Record<AssetClass, string> = {
  crypto: 'hsl(25, 95%, 53%)',
  stock: 'hsl(217, 91%, 60%)',
  commodity: 'hsl(142, 71%, 45%)',
  indicator: 'hsl(270, 70%, 65%)',
  forex: 'hsl(215, 16%, 55%)',
};

export function EmptyChart({ message }: { message?: string }) {
  const { t } = useTranslation('traders');
  return (
    <div className="flex flex-col items-center justify-center h-28 text-center">
      <span className="text-xs text-muted-foreground">{message ?? t('charts.noData')}</span>
    </div>
  );
}

/**
 * Asset split as a ring with a legend.
 *
 * The centre carries the total in the caller's units, because a ring shows
 * proportion and says nothing about magnitude on its own.
 */
export function AssetDonut({
  rows: rawRows,
  format,
  centreCaption,
  onSliceClick,
}: {
  rows: Slice[];
  format: (v: number) => string;
  centreCaption?: string;
  /** Typed slices are clickable when given: the drill-down from types to their assets. */
  onSliceClick?: (s: Slice) => void;
}) {
  const { t } = useTranslation('traders');
  /*
   * One slice per name. SILVERUSDT and XAGUSDT both show as XAG, and the
   * slices are keyed by name: two with one key left React unable to tell
   * them apart, so each $/# click added rows that never went away (Guillaume,
   * 2026-10-05: "it just keeps growing every time I click").
   */
  const merged = new Map<string, Slice>();
  for (const r of rawRows) {
    const m = merged.get(r.symbol);
    merged.set(r.symbol, m ? { ...m, value: m.value + r.value } : { ...r });
  }
  const rows: Slice[] = [...merged.values()].sort((a, b) => b.value - a.value);
  const total = rows.reduce((s, r) => s + r.value, 0);
  if (total <= 0) return <EmptyChart />;

  const shown = rows.slice(0, TOP_SLICES);
  const restValue = rows.slice(TOP_SLICES).reduce((s, r) => s + r.value, 0);
  const slices: Slice[] =
    restValue > 0
      ? [...shown, { symbol: t('charts.more', { n: rows.length - TOP_SLICES }), value: restValue }]
      : shown;

  const colourOf = (s: Slice, i: number) =>
    s.kind ? TYPE_COLORS[s.kind] : s.symbol.startsWith('+') ? REST_COLOR : ASSET_COLORS[i % ASSET_COLORS.length];
  const clickable = (s: Slice) => !!onSliceClick && !!s.kind;

  let offset = 0;
  return (
    <div className="flex flex-wrap items-center justify-center gap-4">
      {/* 2026-09-26: on a narrow card (a ~1000px window, three cards in a row) the
          legend beside the ring was squeezed until "Long"/"Short" vanished and
          "(69%)" was cut. Under 140px it now goes under the ring instead. */}
      <div className="relative w-32 h-32 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          {slices.map((s, i) => {
            const dash = (s.value / total) * CIRCUMFERENCE;
            const el = (
              <circle
                key={s.symbol}
                cx="50" cy="50" r={RADIUS} fill="none"
                stroke={colourOf(s, i)}
                strokeWidth="12"
                strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                strokeDashoffset={-offset}
                className={clickable(s) ? 'cursor-pointer hover:opacity-80' : undefined}
                onClick={clickable(s) ? () => onSliceClick!(s) : undefined}
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-base font-bold">{format(total)}</span>
          <span className="text-[9px] text-muted-foreground">{centreCaption ?? t('charts.total')}</span>
        </div>
      </div>

      {/* One line per row, in columns: name (truncates), amount, share. The
          numbers never shrink; a long name ("Matières premières") gives way
          (2026-10-09). */}
      <div className="flex-1 min-w-[140px] grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 gap-y-1.5 items-center text-xs">
        {slices.map((s, i) => (
          <div
            key={s.symbol}
            className={`col-span-3 grid grid-cols-subgrid items-center ${clickable(s) ? 'cursor-pointer rounded hover:bg-white/5' : ''}`}
            onClick={clickable(s) ? () => onSliceClick!(s) : undefined}
          >
            <span className="flex items-center gap-1.5 min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colourOf(s, i) }} />
            <span className={`truncate whitespace-nowrap min-w-0 ${clickable(s) ? 'underline decoration-dotted underline-offset-2' : ''}`}>
              {s.label ?? <SymbolLabel symbol={s.symbol} badge={false} showName={false} />}
            </span>
            </span>
            {/* The value in the units the caller is showing, and the share it
                represents. A percentage alone cannot say whether 40% is $40k or
                $40M; a value alone cannot say whether it is most of the book. */}
            <span className="font-mono whitespace-nowrap text-end">{format(s.value)}</span>
            <span className="text-muted-foreground font-mono whitespace-nowrap text-end">
              ({fmtPct(Math.round((s.value / total) * 100), 0)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Long against short. Green is always long, red is always short, everywhere. */
export function LongShortDonut({
  longValue,
  shortValue,
  format,
  footnote,
  edge,
  edgeSample,
}: {
  longValue: number;
  shortValue: number;
  format: (v: number) => string;
  footnote?: ReactNode;
  /** Realised PnL by direction, for the bull/bear verdict under the donut.
   *  Omitted where the caller has no closed trades to judge on. */
  edge?: DirectionalEdge;
  /** What the verdict was computed over, when that is less than everything
   *  the trader has done. A verdict over a page of history must say so. */
  edgeSample?: string | null;
}) {
  const { t } = useTranslation('traders');
  const total = longValue + shortValue;
  if (total <= 0) return <EmptyChart />;
  const longShare = (longValue / total) * 100;

  return (
    <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-center gap-4">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={RADIUS} fill="none" stroke={SHORT_COLOR} strokeWidth="12" />
          <circle
            cx="50" cy="50" r={RADIUS} fill="none"
            stroke={LONG_COLOR} strokeWidth="12"
            strokeDasharray={`${(longShare / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-base font-bold">{fmtPct(Math.round(longShare), 0)}</span>
          <span className="text-[9px] text-muted-foreground">{t('charts.long')}</span>
        </div>
      </div>

      <div className="flex-1 min-w-[140px] space-y-1.5">
        <LegendRow
          color={LONG_COLOR} label={t('charts.long')} value={format(longValue)}
          share={total > 0 ? (longValue / total) * 100 : null}
        />
        <LegendRow
          color={SHORT_COLOR} label={t('charts.short')} value={format(shortValue)}
          share={total > 0 ? (shortValue / total) * 100 : null}
        />
        {footnote ? (
          <p className="text-[10px] text-muted-foreground pt-1 border-t border-border/40">{footnote}</p>
        ) : null}
      </div>
      </div>
      {/****************
        * BULL OR BEAR, JUDGED ON WHERE THE MONEY CAME FROM.
        *
        * Guillaume, 2026-09-22: "The Bull/Bear bar is useless, it's just a
        * long/short ratio that we already display in a donut, I taught we
        * were going to make a real assessment of the trader being a bull or
        * a bear."
        *
        * The bar this replaces drew the donut's own number a second way. How
        * OFTEN somebody goes long says nothing about whether they are good at
        * it: a trader can be 80% long and make every dollar short, and the
        * bar called that one "Bull 80%".
        *
        * This splits realised PnL by direction. The word is a claim, so the
        * two numbers behind it sit beside it and can be checked.
        ****************/}
      {edge && edge.verdict !== 'unknown' ? (
        <div className="pt-1.5 border-t border-border/40 space-y-1">
          {/* 2026-09-25: one plain sentence, then one row per side. The old
              "Bear -- makes money short" contradicted the ring above it. */}
          <p className="text-[11px] leading-snug text-foreground">{t(edge.labelKey)}</p>
          {/* Guillaume, 2026-09-25: no header, no trade counts -- the ring
              already shows the split. Just the two results. */}
          <p className="text-[11px] font-mono">
            {edge.longTrades > 0 ? (
              <>
                <span style={{ color: LONG_COLOR }}>{t('charts.long')}</span>{' '}
                <span className={`whitespace-nowrap ${edge.longPnl > 0 ? 'text-success' : edge.longPnl < 0 ? 'text-danger' : 'text-muted-foreground'}`}>
                  {shortUsd(edge.longPnl)}
                </span>
              </>
            ) : null}
            {edge.longTrades > 0 && edge.shortTrades > 0 ? <span className="text-muted-foreground">{t('charts.vs')}</span> : null}
            {edge.shortTrades > 0 ? (
              <>
                <span style={{ color: SHORT_COLOR }}>{t('charts.short')}</span>{' '}
                <span className={`whitespace-nowrap ${edge.shortPnl > 0 ? 'text-success' : edge.shortPnl < 0 ? 'text-danger' : 'text-muted-foreground'}`}>
                  {shortUsd(edge.shortPnl)}
                </span>
              </>
            ) : null}
          </p>
          {edgeSample ? (
            <p className="text-[9px] text-muted-foreground">{edgeSample}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function LegendRow({
  color, label, value, share,
}: { color: string; label: string; value: string; share?: number | null }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <span className="flex-1 min-w-0 truncate">{label}</span>
      <span className="font-mono shrink-0 whitespace-nowrap">{value}</span>
      {share !== null && share !== undefined ? (
        <span className="text-muted-foreground font-mono shrink-0 whitespace-nowrap min-w-9 text-end">
          ({fmtPct(Math.round(share), 0)})
        </span>
      ) : null}
    </div>
  );
}

/**
 * Leverage as a half-dial.
 *
 * The colour bands (green under 10x, amber to 30x, red above) live here for the
 * same reason the palette does: a trader at 40x must not look calmer on one page
 * than on another.
 */
export function LeverageGauge({ leverage, maxLev = 125 }: { leverage: number; maxLev?: number }) {
  const { t } = useTranslation('traders');
  const lev = leverage || 0;
  const pct = Math.min((lev / maxLev) * 100, 100);
  const color = lev < 10 ? LONG_COLOR : lev < 30 ? WARN_COLOR : SHORT_COLOR;

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Wider, not bigger (Guillaume, 10-31): the same height, the arc stretched
          sideways. pathLength=100 makes the dash a plain percentage on any arc. */}
      <div className="relative w-56 h-20 overflow-hidden">
        <svg viewBox="0 0 160 55" className="w-full h-full">
          <path d="M 10 50 A 70 40 0 0 1 150 50" fill="none" stroke={TRACK_COLOR} strokeWidth="8" strokeLinecap="round" />
          <path
            d={valueArcPath(pct)} fill="none" stroke={color} strokeWidth="8" strokeLinecap="butt"
          />
          {/* An odometer's bars and numbers (Guillaume 11-02), inside the same dial. */}
          {dialMarks(maxLev).map((m, i) => (
            <g key={i}>
              <line x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2} stroke="hsl(210, 15%, 55%)" strokeWidth={m.major ? 1.2 : 0.6} strokeLinecap="round" />
              {m.label ? <text x={m.lx} y={m.ly} textAnchor="middle" fontSize="5.5" fill="hsl(210, 15%, 60%)">{m.label}</text> : null}
            </g>
          ))}
          <line
            x1="80" y1="50"
            x2={needleTip(pct).x}
            y2={needleTip(pct).y}
            stroke="hsl(210, 20%, 80%)" strokeWidth="1.5" strokeLinecap="round"
          />
          <circle cx="80" cy="50" r="3" fill="hsl(210, 20%, 80%)" />
        </svg>
      </div>
      <div className="text-center -mt-2">
        <span className="text-2xl font-bold" style={{ color }}>{fmtFixed(lev, 1)}x</span>
        <p className="text-[10px] text-muted-foreground mt-0.5">{t('charts.ofMax', { max: maxLev })}</p>
      </div>
    </div>
  );
}
